import {
  TargetDocumentSync,
  TargetItem,
  ScheduledStrictnessRule,
  EncryptedTargetsEnvelope,
  BehavioralDomainId,
} from './target-types';
import {
  getDefaultTargets,
  getDefaultScheduledRules,
  validateDomainOrPackage,
} from './preset-catalog';
import { incrementVectorClock } from './version-vector';
import {
  reconcileTargetDocuments,
  calculateDocumentSummary,
} from './lww-reconciler';

/**
 * Storage / Firestore Remote Adapter Interface
 */
export interface TargetSyncRepository {
  getTargetDocument(userId: string): Promise<TargetDocumentSync | null>;
  saveTargetDocument(doc: TargetDocumentSync): Promise<void>;
  subscribeToTargets(
    userId: string,
    onUpdate: (doc: TargetDocumentSync) => void
  ): () => void;
}

/**
 * In-Memory & LocalStorage Mock Repository for Testing & Development
 */
export class InMemoryTargetSyncRepository implements TargetSyncRepository {
  private documents = new Map<string, TargetDocumentSync>();
  private listeners = new Map<string, Set<(doc: TargetDocumentSync) => void>>();

  async getTargetDocument(userId: string): Promise<TargetDocumentSync | null> {
    const doc = this.documents.get(userId);
    return doc ? JSON.parse(JSON.stringify(doc)) : null;
  }

  async saveTargetDocument(doc: TargetDocumentSync): Promise<void> {
    this.documents.set(doc.userId, JSON.parse(JSON.stringify(doc)));
    const userListeners = this.listeners.get(doc.userId);
    if (userListeners) {
      userListeners.forEach((fn) => fn(JSON.parse(JSON.stringify(doc))));
    }
  }

  subscribeToTargets(
    userId: string,
    onUpdate: (doc: TargetDocumentSync) => void
  ): () => void {
    if (!this.listeners.has(userId)) {
      this.listeners.set(userId, new Set());
    }
    const set = this.listeners.get(userId)!;
    set.add(onUpdate);
    return () => {
      set.delete(onUpdate);
    };
  }

  seedDocument(doc: TargetDocumentSync) {
    this.documents.set(doc.userId, JSON.parse(JSON.stringify(doc)));
  }
}

/**
 * Zero-Knowledge Cryptographic Helper for Custom Target Lists
 */
export class TargetCryptoManager {
  private cryptoSubtle: SubtleCrypto;

  constructor() {
    this.cryptoSubtle = (globalThis.crypto?.subtle ||
      (typeof window !== 'undefined' && window.crypto?.subtle)) as SubtleCrypto;
  }

  async deriveKeyFromPassphrase(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
    const enc = new TextEncoder();
    const keyMaterial = await this.cryptoSubtle.importKey(
      'raw',
      enc.encode(passphrase),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return this.cryptoSubtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt as any,
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  async generateRandomKey(): Promise<CryptoKey> {
    return this.cryptoSubtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
  }

  async encryptCustomTargets(
    targets: TargetItem[],
    key: CryptoKey
  ): Promise<EncryptedTargetsEnvelope> {
    const customOnly = targets.filter((t) => t.isCustom);
    const jsonStr = JSON.stringify(customOnly);
    const enc = new TextEncoder();
    const data = enc.encode(jsonStr);

    const iv = new Uint8Array(12);
    globalThis.crypto.getRandomValues(iv);

    // AES-GCM encryption with 128-bit authentication tag
    const ciphertextBuffer = await this.cryptoSubtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv as any,
        tagLength: 128,
      },
      key,
      data as any
    );

    // WebCrypto appends the 16-byte tag to the ciphertext
    const ctBytes = new Uint8Array(ciphertextBuffer);
    const tag = ctBytes.slice(ctBytes.length - 16);
    const actualCiphertext = ctBytes.slice(0, ctBytes.length - 16);

    return {
      ciphertextBase64: Buffer.from(actualCiphertext).toString('base64'),
      ivBase64: Buffer.from(iv).toString('base64'),
      tagBase64: Buffer.from(tag).toString('base64'),
      encryptedAt: Date.now(),
    };
  }

  async decryptCustomTargets(
    envelope: EncryptedTargetsEnvelope,
    key: CryptoKey
  ): Promise<TargetItem[]> {
    const ctBytes = Buffer.from(envelope.ciphertextBase64, 'base64');
    const tagBytes = Buffer.from(envelope.tagBase64, 'base64');
    const iv = Buffer.from(envelope.ivBase64, 'base64');

    // Combine ciphertext and tag for WebCrypto decrypt
    const combined = new Uint8Array(ctBytes.length + tagBytes.length);
    combined.set(ctBytes);
    combined.set(tagBytes, ctBytes.length);

    const decryptedBuffer = await this.cryptoSubtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv as any,
        tagLength: 128,
      },
      key,
      combined as any
    );

    const dec = new TextDecoder();
    const jsonStr = dec.decode(decryptedBuffer);
    return JSON.parse(jsonStr) as TargetItem[];
  }
}

/**
 * Main Encrypted Target Synchronization Manager
 */
export class EncryptedTargetSyncManager {
  private userId: string;
  private deviceId: string;
  private role: 'self' | 'clinician' | 'family_guardian';
  private repo: TargetSyncRepository;
  private cryptoManager: TargetCryptoManager;
  private currentDoc: TargetDocumentSync;
  private isOnline: boolean = true;
  private offlineMutationQueue: Array<(doc: TargetDocumentSync) => TargetDocumentSync> = [];
  private unsubscribeRemote?: () => void;
  private stateListeners = new Set<(doc: TargetDocumentSync) => void>();

  constructor(options: {
    userId: string;
    deviceId: string;
    role?: 'self' | 'clinician' | 'family_guardian';
    repository?: TargetSyncRepository;
    initialDocument?: TargetDocumentSync;
  }) {
    this.userId = options.userId;
    this.deviceId = options.deviceId;
    this.role = options.role || 'self';
    this.repo = options.repository || new InMemoryTargetSyncRepository();
    this.cryptoManager = new TargetCryptoManager();

    if (options.initialDocument) {
      this.currentDoc = JSON.parse(JSON.stringify(options.initialDocument));
    } else {
      const defaultTargets = getDefaultTargets();
      const defaultRules = getDefaultScheduledRules();
      this.currentDoc = {
        schemaVersion: 2,
        userId: this.userId,
        version: 1,
        vectorClock: { [this.deviceId]: 1 },
        updatedAt: new Date().toISOString(),
        lastModifiedBy: this.role,
        deviceId: this.deviceId,
        summary: calculateDocumentSummary(defaultTargets),
        targets: defaultTargets,
        scheduledRules: defaultRules,
      };
    }
  }

  getDocument(): TargetDocumentSync {
    return JSON.parse(JSON.stringify(this.currentDoc));
  }

  getCryptoManager(): TargetCryptoManager {
    return this.cryptoManager;
  }

  setOnlineStatus(online: boolean) {
    this.isOnline = online;
    if (this.isOnline && this.offlineMutationQueue.length > 0) {
      this.flushOfflineQueue();
    }
  }

  subscribe(listener: (doc: TargetDocumentSync) => void): () => void {
    this.stateListeners.add(listener);
    listener(this.getDocument());
    return () => {
      this.stateListeners.delete(listener);
    };
  }

  private notifyListeners() {
    const snapshot = this.getDocument();
    this.stateListeners.forEach((fn) => fn(snapshot));
  }

  async startLiveSync(): Promise<void> {
    // Initial fetch
    const remoteDoc = await this.repo.getTargetDocument(this.userId);
    if (remoteDoc) {
      this.handleIncomingRemoteDocument(remoteDoc);
    } else {
      // Seed remote with initial local doc
      await this.repo.saveTargetDocument(this.currentDoc);
    }

    // Subscribe to remote updates
    this.unsubscribeRemote = this.repo.subscribeToTargets(this.userId, (updatedDoc) => {
      this.handleIncomingRemoteDocument(updatedDoc);
    });
  }

  stopLiveSync(): void {
    if (this.unsubscribeRemote) {
      this.unsubscribeRemote();
      this.unsubscribeRemote = undefined;
    }
  }

  handleIncomingRemoteDocument(remoteDoc: TargetDocumentSync): void {
    // Ignore updates that originate from this device if we are already ahead
    if (remoteDoc.deviceId === this.deviceId && remoteDoc.version <= this.currentDoc.version) {
      return;
    }

    const { document: reconciled, resolutionType } = reconcileTargetDocuments(
      this.currentDoc,
      remoteDoc,
      this.deviceId
    );

    this.currentDoc = reconciled;
    this.notifyListeners();

    // If a concurrent conflict was reconciled, push the newly resolved state back to repository
    if (resolutionType === 'CONCURRENT_MERGED' && this.isOnline) {
      this.repo.saveTargetDocument(this.currentDoc).catch((err) => {
        console.error('Failed to publish merged document:', err);
      });
    }
  }

  private async applyMutation(
    mutationFn: (doc: TargetDocumentSync) => void
  ): Promise<TargetDocumentSync> {
    const updatedTargets = [...this.currentDoc.targets];
    const updatedRules = [...this.currentDoc.scheduledRules];

    const draft: TargetDocumentSync = {
      ...this.currentDoc,
      targets: updatedTargets,
      scheduledRules: updatedRules,
    };

    mutationFn(draft);

    draft.version = (this.currentDoc.version || 0) + 1;
    draft.vectorClock = incrementVectorClock(this.currentDoc.vectorClock, this.deviceId);
    draft.updatedAt = new Date().toISOString();
    draft.lastModifiedBy = this.role;
    draft.deviceId = this.deviceId;
    draft.summary = calculateDocumentSummary(draft.targets);

    this.currentDoc = draft;
    this.notifyListeners();

    if (this.isOnline) {
      await this.repo.saveTargetDocument(this.currentDoc);
    } else {
      this.offlineMutationQueue.push((d) => draft);
    }

    return this.getDocument();
  }

  private async flushOfflineQueue(): Promise<void> {
    if (this.offlineMutationQueue.length === 0) return;
    this.offlineMutationQueue = [];
    if (this.isOnline) {
      await this.repo.saveTargetDocument(this.currentDoc);
    }
  }

  // --- High-Level Mutation Operations ---

  async toggleTarget(targetId: string, enabled?: boolean): Promise<TargetDocumentSync> {
    return this.applyMutation((doc) => {
      const now = Date.now();
      doc.targets = doc.targets.map((t) => {
        if (t.id === targetId) {
          return {
            ...t,
            enabled: enabled !== undefined ? enabled : !t.enabled,
            updatedAt: now,
          };
        }
        return t;
      });
    });
  }

  async addCustomTarget(
    category: BehavioralDomainId,
    nameOrDomain: string
  ): Promise<{ success: boolean; target?: TargetItem; error?: string }> {
    const validation = validateDomainOrPackage(nameOrDomain);
    if (!validation.isValid) {
      return { success: false, error: validation.errorMessage };
    }

    const clean = validation.cleanIdentifier;
    const existing = this.currentDoc.targets.find(
      (t) => t.category === category && (t.packageOrDomain === clean || t.name.toLowerCase() === clean)
    );

    if (existing) {
      // Re-enable if disabled
      if (!existing.enabled) {
        await this.toggleTarget(existing.id, true);
        return { success: true, target: existing };
      }
      return { success: false, error: 'Target is already added and active.' };
    }

    const newTarget: TargetItem = {
      id: `custom_${category}_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      category,
      name: clean,
      packageOrDomain: clean,
      alternativeDomain: clean.includes('.') ? clean : undefined,
      isCustom: true,
      enabled: true,
      updatedAt: Date.now(),
      tags: ['custom', 'user_defined'],
    };

    await this.applyMutation((doc) => {
      doc.targets.push(newTarget);
    });

    return { success: true, target: newTarget };
  }

  async removeTarget(targetId: string): Promise<TargetDocumentSync> {
    return this.applyMutation((doc) => {
      doc.targets = doc.targets.filter((t) => t.id !== targetId);
    });
  }

  async setMasterCategoryEnabled(
    category: BehavioralDomainId,
    enabled: boolean
  ): Promise<TargetDocumentSync> {
    const now = Date.now();
    return this.applyMutation((doc) => {
      doc.targets = doc.targets.map((t) => {
        if (t.category === category) {
          return { ...t, enabled, updatedAt: now };
        }
        return t;
      });
    });
  }

  async updateScheduledRule(
    ruleUpdates: Partial<ScheduledStrictnessRule> & { categoryId: string }
  ): Promise<TargetDocumentSync> {
    return this.applyMutation((doc) => {
      let found = false;
      const sanitizedUpdates = { ...ruleUpdates };
      if (sanitizedUpdates.inactivityTimeoutMinutes !== undefined) {
        sanitizedUpdates.inactivityTimeoutMinutes = Math.max(15, sanitizedUpdates.inactivityTimeoutMinutes);
      }
      if (sanitizedUpdates.continuousAllowanceMinutes !== undefined) {
        sanitizedUpdates.continuousAllowanceMinutes = Math.max(20, sanitizedUpdates.continuousAllowanceMinutes);
      }

      doc.scheduledRules = doc.scheduledRules.map((r) => {
        if (r.categoryId === ruleUpdates.categoryId) {
          found = true;
          return { ...r, ...sanitizedUpdates };
        }
        return r;
      });

      if (!found) {
        doc.scheduledRules.push({
          categoryId: ruleUpdates.categoryId,
          daytimeTier: ruleUpdates.daytimeTier || 'softPause',
          nighttimeTier: ruleUpdates.nighttimeTier || 'hardLock',
          nightStartHour: ruleUpdates.nightStartHour ?? 22,
          nightEndHour: ruleUpdates.nightEndHour ?? 6,
          allowOverride: ruleUpdates.allowOverride ?? true,
          isCurfewLocked: ruleUpdates.isCurfewLocked ?? false,
          inactivityTimeoutMinutes: sanitizedUpdates.inactivityTimeoutMinutes ?? 15,
          continuousAllowanceMinutes: sanitizedUpdates.continuousAllowanceMinutes ?? 20,
        });
      }
    });
  }

  async updateTargetLimits(
    targetId: string,
    limits: { inactivityTimeoutMinutes?: number; continuousAllowanceMinutes?: number }
  ): Promise<TargetDocumentSync> {
    const now = Date.now();
    const sanitizedInactivity =
      limits.inactivityTimeoutMinutes !== undefined
        ? Math.max(15, limits.inactivityTimeoutMinutes)
        : undefined;
    const sanitizedContinuous =
      limits.continuousAllowanceMinutes !== undefined
        ? Math.max(20, limits.continuousAllowanceMinutes)
        : undefined;

    return this.applyMutation((doc) => {
      doc.targets = doc.targets.map((t) => {
        if (t.id === targetId) {
          return {
            ...t,
            inactivityTimeoutMinutes: sanitizedInactivity,
            continuousAllowanceMinutes: sanitizedContinuous,
            updatedAt: now,
          };
        }
        return t;
      });
    });
  }
}
